import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const AISummarySection = ({ analysisData, onSave }) => {
    const [aiSummary, setAiSummary] = useState(analysisData?.aiSummary || '');
    const [aiInsights, setAiInsights] = useState(analysisData?.aiInsights || '');
    const [isEditing, setIsEditing] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        setAiSummary(analysisData?.aiSummary || '');
        setAiInsights(analysisData?.aiInsights || '');
    }, [analysisData]);

    const handleGenerateAISummary = async () => {
        setIsGenerating(true);
        try {
            // Get image count from either source (productImages from scanner or images from database)
            const imageCount = analysisData?.productImages?.total || analysisData?.images?.total || 0;

            // This will integrate with Gemini API in the future
            // For now, placeholder for AI generation
            const placeholderSummary = `
Website Analysis Summary:
This website presents a professional online presence with a modern design approach.
The brand uses a consistent color scheme (${analysisData?.themeColors?.primary || 'primary color not detected'}) 
and features ${imageCount} product images to showcase their offerings.
            `.trim();

            const placeholderInsights = `
Key Insights:
- Brand Identity: Strong visual branding with consistent color usage
- User Experience: ${imageCount} images optimized for engagement
- Content Strategy: Multiple product pages indicate e-commerce or service-based model
- Recommended Improvements: Ensure all product images have descriptive alt text for accessibility
            `.trim();

            setAiSummary(placeholderSummary);
            setAiInsights(placeholderInsights);
        } catch (error) {
            console.error('Error generating AI summary:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSaveChanges = () => {
        if (onSave) {
            onSave({
                aiSummary,
                aiInsights
            });
        }
        setIsEditing(false);
    };

    return (
        <div className="ai-summary-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>AI-Generated Summary & Insights</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={handleGenerateAISummary}
                        disabled={isGenerating}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#06b6d4',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isGenerating ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            opacity: isGenerating ? 0.6 : 1,
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isGenerating ? 'Generating...' : 'Generate'}
                    </button>
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: isEditing ? '#ef4444' : '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                </div>
            </div>

            {isEditing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '13px' }}>
                            Business Summary
                        </label>
                        <textarea
                            value={aiSummary}
                            onChange={(e) => setAiSummary(e.target.value)}
                            placeholder="Edit the AI-generated summary..."
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #7dd3fc',
                                borderRadius: '6px',
                                fontSize: '13px',
                                minHeight: '120px',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                backgroundColor: 'white'
                            }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '13px' }}>
                            Key Insights
                        </label>
                        <textarea
                            value={aiInsights}
                            onChange={(e) => setAiInsights(e.target.value)}
                            placeholder="Edit key insights..."
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #7dd3fc',
                                borderRadius: '6px',
                                fontSize: '13px',
                                minHeight: '120px',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                backgroundColor: 'white'
                            }}
                        />
                    </div>
                    <button
                        onClick={handleSaveChanges}
                        style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Save Changes
                    </button>
                </div>
            ) : (
                <div>
                    {aiSummary ? (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4>Summary</h4>
                            <p style={{ margin: '0', whiteSpace: 'pre-wrap' }}>
                                {aiSummary}
                            </p>
                        </div>
                    ) : (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <p style={{ margin: '0', fontStyle: 'italic', color: '#475569' }}>
                                Click "Generate" to create an AI-powered summary of this website...
                            </p>
                        </div>
                    )}

                    {aiInsights && (
                        <div>
                            <h4>Key Insights</h4>
                            <p style={{ margin: '0', whiteSpace: 'pre-wrap' }}>
                                {aiInsights}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

AISummarySection.propTypes = {
    analysisData: PropTypes.object,
    onSave: PropTypes.func
};

export default AISummarySection;
